// The front page of a section (Master Data, Reports, Governance, Grow, Settings): every
// page this person holds under that top-level menu, as CARDS, grouped by subject.
// One card per page, the same compact card as the home screen (components/cards/
// CompactCard): its icon, its name and its figures. The whole card is the link. The
// page's description is the card's hover title, and the find box searches all of it.
// The subject groups keep their names and order and sit side by side in two columns
// from 1280px wide, so most front pages fit on one screen.
//
// The MENU decides what is listed: a page's name, icon, address and order are its menu
// row's, and a page the person does not hold is never shown. The catalogue only
// describes: the subject heading and the line, found by the page's address. A page the
// catalogue does not know is still listed, under "Other".
//
// Holding a page's menu row is not a promise of access. Some pages check a second
// permission and show their own "access needed" panel; that is left to the page.
//
// A card may carry one or two figures from the page it opens ("1,240 active stores ·
// 86 short of staff"), worked out by the server with that page's own rules for the
// person asking. They are asked for after the page is drawn, quick and slow figures at
// once; a grey bar holds their place until a card's answer arrives, and a card whose
// figure cannot be worked out says a grey "Open".
import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Skeleton } from '../components/Skeleton';
import CompactCard, { type CompactFigure } from '../components/cards/CompactCard';
import { useAuthStore } from '../stores/authStore';
import { useMenuStore, type MenuItem } from '../stores/menuStore';
import { normalisePath, pageInfoFor, sectionByIndexPath, sectionByMenuCode, type SectionInfo } from '../navigation/catalog';
import { DASHBOARD_HOME_PATH } from '../navigation/dashboards';
import { holdsDashboard, pageMenuItems, pagesUnder } from '../navigation/menuTree';
import { menuIcon } from '../navigation/menuIcon';
import { SECTION_STATS_MAX_PATHS, sectionStatsAPI, type CardStat, type SectionStatsSpeed } from '../services/api';
import NotFoundPage from './NotFoundPage';

const OTHER = 'Other';

// The page sits on the shell's soft canvas, centred, with the picture's margins: the
// shell's own padding plus this comes to 32px all round up to a 1400px-wide page.
const PAGE = 'mx-auto w-full max-w-[1352px] pb-2 pt-4 sm:px-2';
// The subject groups: one under another up to a small laptop, and from 1280px side by
// side in two columns, read down the first and then down the second, so the
// catalogue's order is kept. A group is never split across the two columns.
const GROUPS = 'mt-5 xl:columns-2 xl:gap-x-5';
const GROUP = 'mb-5 break-inside-avoid';
const GROUP_HEADING = 'mb-2 ml-0.5 text-xs font-semibold uppercase tracking-wider text-gray-500';
// Inside a group: one card per row on a phone, two from a large phone, three on a small
// laptop (one group across the page), two once the groups sit side by side: four cards
// across on a wide screen, the steps of the home screen's grid (COMPACT_CARD_GRID).
const GROUP_GRID = 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-2';
const MESSAGE = 'rounded-2xl border border-gray-200 bg-white p-5 shadow-sm';

interface PageEntry {
  row: MenuItem;
  path: string;
  /** The whole description: the card's hover title, and what the find box searches. */
  line: string | null;
  /** Lower-cased name, line and keywords: what the find box searches. */
  haystack: string;
}

export interface SectionPanel {
  name: string;
  /** What the subject covers. Not drawn: it is the heading's hover text. */
  blurb: string;
  pages: PageEntry[];
}

/**
 * A section's pages as panels: the catalogue's subject headings in their order, then
 * "Other" for a page the catalogue does not know or files under a heading this section
 * does not have. Pages keep menu order inside a panel; an empty panel is dropped.
 */
export function sectionPanels(section: SectionInfo, rows: MenuItem[]): SectionPanel[] {
  const listed = new Map<string, SectionPanel>(
    section.groups.map((g) => [g.name, { name: g.name, blurb: g.blurb, pages: [] }]),
  );
  const other: SectionPanel = { name: OTHER, blurb: '', pages: [] };
  for (const row of rows) {
    // A row without an address is a heading in the menu, not a page.
    if (!row.path) continue;
    const info = pageInfoFor(row.path);
    // No catalogue line: fall back to what the menu row says about itself, if anything.
    const line = info?.line ?? (row.description?.trim() || null);
    const haystack = [row.name, line, info?.keywords].filter(Boolean).join(' ').toLowerCase();
    const panel = (info && listed.get(info.group)) || other;
    panel.pages.push({ row, path: row.path, line, haystack });
  }
  return [...listed.values(), other].filter((p) => p.pages.length > 0);
}

/** Narrow the panels to the pages that contain every word typed. */
export function findInPanels(panels: SectionPanel[], query: string): SectionPanel[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return panels;
  return panels
    .map((p) => ({ ...p, pages: p.pages.filter((e) => words.every((w) => e.haystack.includes(w))) }))
    .filter((p) => p.pages.length > 0);
}

/**
 * The section's name, plain. What the section is for (its short line and its longer
 * purpose) is the title's hover text, not a line on the page.
 */
function SectionTitle({ top, hover }: { top: MenuItem; hover?: string }): ReactElement {
  return (
    <h1 title={hover} className="min-w-0 break-words text-[26px] font-bold leading-9 tracking-tight text-gray-900">
      {top.name}
    </h1>
  );
}

/**
 * A card's figures for each page address. A page not in it is still coming; null is no
 * number. The whole map is null when there is nothing to ask (no project chosen).
 */
type Figures = Record<string, CardStat | null>;

/** What the server sent for one page, if it is something a card can draw; else no number. */
function drawable(stat: unknown): CardStat | null {
  const parts = (stat as CardStat | null)?.parts;
  if (!Array.isArray(parts) || parts.length === 0) return null;
  const ok = parts.every(
    (p) =>
      typeof p?.label === 'string' &&
      ((typeof p.value === 'number' && Number.isFinite(p.value)) || (typeof p.value === 'string' && p.value !== '')),
  );
  return ok ? (stat as CardStat) : null;
}

/**
 * The figures for every page on this front page, for the chosen project: the quick and
 * the slow ones asked at once, in calls of at most SECTION_STATS_MAX_PATHS pages. Once a
 * call's two answers are in (or failed), a page neither of them carried has no number.
 * An answer for another project or section than the one now shown is dropped.
 */
function useCardFigures(sectionId: string, projectId: number | null, paths: string[]): Figures | null {
  const pathsKey = paths.join('\n');
  const key = projectId == null || paths.length === 0 ? '' : `${sectionId}\u0000${projectId}\u0000${pathsKey}`;
  const [got, setGot] = useState<{ key: string; figures: Figures }>({ key: '', figures: {} });

  useEffect(() => {
    if (!key || projectId == null) return undefined;
    let live = true;
    const all = pathsKey.split('\n');
    // Only ever adds to what is known for THIS key; a figure already drawn is never
    // taken back by a later null.
    const merge = (found: Figures) => {
      if (!live) return;
      setGot((prev) => {
        const base = prev.key === key ? prev.figures : {};
        const next = { ...base };
        for (const [path, stat] of Object.entries(found)) {
          if (stat || !(path in next)) next[path] = stat;
        }
        return { key, figures: next };
      });
    };
    for (let i = 0; i < all.length; i += SECTION_STATS_MAX_PATHS) {
      const batch = all.slice(i, i + SECTION_STATS_MAX_PATHS);
      const ask = (speed: SectionStatsSpeed) =>
        sectionStatsAPI.get(projectId, sectionId, batch, speed).then(
          (answer) => {
            const stats = answer?.data?.stats;
            if (!stats || typeof stats !== 'object') return;
            const found: Figures = {};
            for (const path of batch) {
              if (Object.prototype.hasOwnProperty.call(stats, path)) found[path] = drawable(stats[path]);
            }
            merge(found);
          },
          // A failed call is no number for its cards, and no error on the page.
          () => undefined,
        );
      void Promise.all([ask('fast'), ask('slow')]).then(() => {
        merge(Object.fromEntries(batch.map((path) => [path, null])));
      });
    }
    return () => {
      live = false;
    };
    // `key` is the section, the project and the pages: a new one of any asks again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!key) return null;
  return got.key === key ? got.figures : {};
}

/**
 * A page's figures as the card draws them: in the server's order, a figure that waits on
 * someone in amber. The card draws the first two; a third (Employees' "left") is in its
 * hover. A 0 is drawn light grey by the card itself.
 */
function cardFigures(stat: CardStat): CompactFigure[] {
  return stat.parts.map((p) => ({ value: p.value, label: p.label, tone: p.warn ? 'action' : 'plain' }));
}

/**
 * One page: the compact card. `figure` undefined = still coming (the grey bar), null =
 * no number ("Open"). The page's description is the card's hover title.
 */
function PageCard({ entry, figure }: { entry: PageEntry; figure: CardStat | null | undefined }): ReactElement {
  const { row, path, line } = entry;
  return (
    <CompactCard
      to={path}
      icon={menuIcon(row.icon)}
      name={row.name}
      figures={figure ? cardFigures(figure) : []}
      loading={figure === undefined}
      hover={line ?? undefined}
    />
  );
}

function SectionPages({ section, top, rows }: { section: SectionInfo; top: MenuItem; rows: MenuItem[] }): ReactElement {
  const [query, setQuery] = useState('');
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const panels = useMemo(() => sectionPanels(section, rows), [section, rows]);
  const shown = useMemo(() => findInPanels(panels, query), [panels, query]);
  // Every page on the front page, not only those the find box leaves: typing must not ask again.
  // Asked for, and looked up, by the catalogue's address (the server's registries are keyed
  // by it): a menu row may still carry an old alias, a query string or a trailing slash.
  const paths = useMemo(() => [...new Set(panels.flatMap((p) => p.pages.map((e) => normalisePath(e.path))))], [panels]);
  const figures = useCardFigures(section.id, selectedProjectId ?? null, paths);
  const findLabel = `Find in ${top.name}`;

  return (
    <div className={PAGE}>
      {/* The title on the left, the find box on the right; on a phone the box drops
          under the title. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <SectionTitle top={top} hover={section.purpose} />
        <div className="relative w-full shrink-0 sm:w-72">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={findLabel}
            aria-label={findLabel}
            className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
          Nothing in {top.name} matches “{query.trim()}”.{' '}
          <button
            type="button"
            onClick={() => setQuery('')}
            className="font-semibold text-primary-600 hover:text-primary-700 focus:outline-none focus-visible:underline"
          >
            Show every page
          </button>
        </div>
      ) : (
        // The groups in the catalogue's order, side by side from 1280px: each a small
        // grey heading over its own two-across grid of cards.
        <div className={GROUPS}>
          {shown.map((panel) => (
            <section key={panel.name} aria-label={panel.name} className={GROUP}>
              <h2 title={panel.blurb || undefined} className={GROUP_HEADING}>
                {panel.name}
              </h2>
              <ul className={GROUP_GRID}>
                {panel.pages.map((entry) => (
                  <li key={entry.row.id} className="min-w-0">
                    <PageCard entry={entry} figure={figures ? figures[normalisePath(entry.path)] : null} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/** Grey shapes in the page's own shape: the title, and two columns of groups of cards. */
function Loading(): ReactElement {
  const group = (cards: number, key: string) => (
    <div key={key} className={GROUP}>
      <Skeleton className="mb-2 h-4 w-32" />
      <div className={GROUP_GRID}>
        {Array.from({ length: cards }, (_, i) => (
          <Skeleton key={i} className="h-24 rounded-[14px]" />
        ))}
      </div>
    </div>
  );
  return (
    <div className={PAGE} role="status" aria-label="Loading">
      <Skeleton className="h-9 w-48" />
      <div className={GROUPS}>{[group(3, 'a'), group(2, 'b'), group(2, 'c'), group(3, 'd')]}</div>
    </div>
  );
}

/** Shown when the person does not hold the section, or holds it with no page in it. */
function NoPages({ top, dashboardName }: { top: MenuItem | null; dashboardName: string | null }): ReactElement {
  return (
    <div className={PAGE}>
      {top && <SectionTitle top={top} />}
      <div className={`${top ? 'mt-5 ' : ''}${MESSAGE}`}>
        <p className="text-sm text-gray-700">
          You do not have any pages in {top?.name ?? 'this section'} yet. Ask your project admin if you need one.
        </p>
        {dashboardName && (
          <Link
            to={DASHBOARD_HOME_PATH}
            className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700 focus:outline-none focus-visible:underline"
          >
            Back to {dashboardName}
          </Link>
        )}
      </div>
    </div>
  );
}

export default function SectionFrontPage(): ReactElement {
  const { pathname } = useLocation();
  const section = sectionByIndexPath(pathname);
  const items = useMenuStore((s) => s.items);
  const loaded = useMenuStore((s) => s.loaded);
  const isSuperAdmin = useAuthStore((s) => s.user?.adminRole === 'super_admin');
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);

  const pages = useMemo(
    () => pageMenuItems(items, { isSuperAdmin, selectedProjectId }),
    [items, isSuperAdmin, selectedProjectId],
  );
  const top = useMemo(
    () => (section ? pages.find((m) => !m.parent_id && sectionByMenuCode(m.code)?.id === section.id) ?? null : null),
    [pages, section],
  );
  const rows = useMemo(() => (top ? pagesUnder(pages, top) : []), [pages, top]);

  // Only reachable if a route is pointed here for an address that is not a front page.
  if (!section) return <NotFoundPage />;
  if (!loaded) return <Loading />;
  if (!top || rows.length === 0) {
    // The way back is offered only to someone who holds the Dashboard, under its menu name.
    const dashboardName = holdsDashboard(items)
      ? pages.find((m) => m.path === DASHBOARD_HOME_PATH)?.name || 'Dashboard'
      : null;
    return <NoPages top={top} dashboardName={dashboardName} />;
  }
  // Keyed by section: the five front pages share one route element, and what was typed
  // in "Find in Reports" must not still be narrowing Settings.
  return <SectionPages key={section.id} section={section} top={top} rows={rows} />;
}
